import useSWR from "swr";
import { unauthAxiosFetcher } from "../axios";

// Curated home-page surfaces shared with the expo Nearby screen. Both
// surfaces read from these endpoints so the Soclub carousel + (forthcoming)
// shared sections never drift across web and app. Project IDs and Soclub
// subtitle live BE-side in service/featured.js — do NOT hardcode them here.

export const useFeaturedSoclub = (community) =>
  useSWR(["/v1/featured/soclub", community || null], ([url]) => unauthAxiosFetcher(url));

const NEARBY_LOCATION_STORAGE_KEY = "sotravel_nearby_location_v1";
const NEARBY_LOCATION_TTL_MS = 24 * 60 * 60 * 1000;

const ADMIN_COMMUNITY_TEST_LOCATIONS = {
  sg: { lat: 1.3521, lng: 103.8198, city: "Singapore", country: "Singapore" },
  my: { lat: 3.139, lng: 101.6869, city: "Kuala Lumpur", country: "Malaysia" },
  tw: { lat: 25.033, lng: 121.5654, city: "Taipei", country: "Taiwan" },
  id: { lat: -6.2088, lng: 106.8456, city: "Jakarta", country: "Indonesia" },
};

export const setAdminNearbyLocationForCommunity = (community) => {
  if (typeof window === "undefined") return;
  const location = ADMIN_COMMUNITY_TEST_LOCATIONS[community];
  if (!location) return;

  window.localStorage.setItem(
    NEARBY_LOCATION_STORAGE_KEY,
    JSON.stringify({
      ...location,
      expiresAt: Date.now() + NEARBY_LOCATION_TTL_MS,
      source: "admin-community-test",
    })
  );
};

const getStoredNearbyLocation = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(NEARBY_LOCATION_STORAGE_KEY);
    if (!raw) return null;

    const value = JSON.parse(raw);
    if (
      !Number.isFinite(Number(value?.lat)) ||
      !Number.isFinite(Number(value?.lng)) ||
      !Number.isFinite(Number(value?.expiresAt)) ||
      Number(value.expiresAt) <= Date.now()
    ) {
      window.localStorage.removeItem(NEARBY_LOCATION_STORAGE_KEY);
      return null;
    }

    return { lat: Number(value.lat), lng: Number(value.lng) };
  } catch {
    return null;
  }
};

const saveNearbyLocation = (location) => {
  if (typeof window === "undefined") return;

  const lat = Number(location?.lat);
  const lng = Number(location?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

  try {
    window.localStorage.setItem(
      NEARBY_LOCATION_STORAGE_KEY,
      JSON.stringify({ lat, lng, expiresAt: Date.now() + NEARBY_LOCATION_TTL_MS })
    );
  } catch {
    // Storage can be disabled or full; the API still works using IP fallback.
  }
};

const fetchNearbyEvents = async ([url, userId]) => {
  const location = getStoredNearbyLocation();
  const query = location ? `?lat=${location.lat}&lng=${location.lng}` : "";
  const response = await unauthAxiosFetcher(`${url}${query}`);

  // The first request is resolved by backend IP geolocation. Persist only the
  // approximate coordinates returned by the backend, never the user's IP.
  if (!location && response?.location) saveNearbyLocation(response.location);
  return response;
};

// "Events near you" page: every upcoming event (one row per event at its
// next date) shaped for the Luma-style timeline — time, host, venue, pill
// fields and the going faces. The user id only changes whose faces are left
// out (people the viewer blocked), so it is part of the key.
export const useClubEventsList = (userId) =>
  useSWR(["/v1/featured/club-events/nearby", userId || null], fetchNearbyEvents, {
    revalidateOnFocus: false,
  });
